/* ============================================================
   Fence Works — the concrete reports (docs/SERVICE_SWITCHER_PLAN.md §13
   item 17). Pure, import-free: the unit harness transpiles it alone.

   What a concrete owner asks the numbers: which job type makes the money,
   what a lift really costs in foam per sq ft-inch, where the leads come from
   and which of them close, how the crew's hours split across the work — and,
   for the account that runs both services, how fence and concrete compare.

   The inputs are what the app already has: each won quote's priced blocks
   (frozen at the win as q.wonSnapshot — app.tsx stampQuoteDoc — so a rate
   card changed later doesn't rewrite history), the lines the customer
   signed (q.signedDoc.lines, keyed by job type: "lift", "lift-2", "caulk"),
   the crew's clocked hours and lift log on the work order. Nothing here
   re-prices anything.
   ============================================================ */
export type BlockRow = { quoteId?: string; jobType: string; drop?: string | null; amount: number; cost: number; profit: number; laborHours: number; actualHours?: number; foamLb?: number; sqftInch?: number };
export type JobTypeRow = { jobType: string; quotes: number; lines: number; revenue: number; cost: number; profit: number; pct: number; estHours: number; actualHours: number; foamLb: number; sqftInch: number; foamPerSqftInch: number | null; foamCostPerSqftInch: number | null };

const r2 = (n: number) => Math.round((Number(n) || 0) * 100) / 100;
const num = (x: any) => (x == null || x === "" || !isFinite(Number(x)) ? 0 : Number(x));
export const JOB_ORDER = ["lift", "coat", "clean", "seal", "caulk"];

/* "lift-2" → "lift"; the first release's single "base" line can't be split */
export function lineJobType(key: any): string | null {
  const k = String(key || "").replace(/-\d+$/, "");
  return JOB_ORDER.includes(k) ? k : null;
}
/* when a quote was won: the stamp the store writes, else the signature, else when it was made */
export function wonAtOf(q: any): number {
  if (!q) return 0;
  return num(q.wonAt) || num(q.signedAt) || num(q.signature && q.signature.at) || num(q.createdAt);
}
export function inPeriod(q: any, from: number): boolean { return wonAtOf(q) >= (num(from) || 0); }

/* the revenue the customer actually signed for, per job type, from the signed
   lines (overrides, discounts and the typed total all live there). null when
   the lines can't be split (a "base"-only quote, or no signed lines). */
export function signedRevenueByJobType(lines: any): Record<string, number> | null {
  if (!Array.isArray(lines) || !lines.length) return null;
  const out: Record<string, number> = {};
  let any = false;
  for (const l of lines) {
    if (!l) continue;
    const jt = lineJobType(l.key);
    if (!jt) { if (String(l.key || "") === "base") return null; continue; }   // a discount / adjustment line is left out; "base" means unsplittable
    out[jt] = r2((out[jt] || 0) + num(l.amount));
    any = true;
  }
  return any ? out : null;
}

/* the priced blocks with their amounts re-based to what was signed, per job
   type (the engine's split inside a job type is kept); profit follows */
export function rebaseBlocks(blocks: any, signedByType: Record<string, number> | null): BlockRow[] {
  const rows: BlockRow[] = (Array.isArray(blocks) ? blocks : []).filter((b: any) => b && JOB_ORDER.includes(b.jobType)).map((b: any) => ({
    quoteId: b.quoteId, jobType: b.jobType, drop: b.drop == null ? null : String(b.drop), amount: num(b.amount), cost: num(b.cost), profit: num(b.profit), laborHours: num(b.laborHours),
  }));
  if (!signedByType) return rows;
  const sums: Record<string, number> = {};
  rows.forEach((r) => { sums[r.jobType] = (sums[r.jobType] || 0) + r.amount; });
  return rows.map((r) => {
    if (signedByType[r.jobType] == null) return r;
    const share = sums[r.jobType] > 0 ? r.amount / sums[r.jobType] : 1 / rows.filter((x) => x.jobType === r.jobType).length;
    const amount = r2(signedByType[r.jobType] * share);
    return { ...r, amount, profit: r2(amount - r.cost) };
  });
}

/* the crew's clocked hours on a job, split across its blocks by each block's
   share of the estimated labor (equal shares when nothing was estimated) */
export function allocateHours(actualHours: number, blocks: BlockRow[]): BlockRow[] {
  const total = num(actualHours);
  const est = blocks.reduce((s, b) => s + num(b.laborHours), 0);
  return blocks.map((b) => ({ ...b, actualHours: !blocks.length ? 0 : est > 0 ? r2(total * (num(b.laborHours) / est)) : r2(total / blocks.length) }));
}

/* the rows per job type, best earner first */
export function jobTypeRows(rows: BlockRow[], opts: { foamCostPerLb?: number } = {}): JobTypeRow[] {
  const by: Record<string, JobTypeRow & { _q: Set<string> }> = {};
  (rows || []).forEach((r) => {
    if (!r || !JOB_ORDER.includes(r.jobType)) return;
    const g = by[r.jobType] || (by[r.jobType] = { jobType: r.jobType, quotes: 0, lines: 0, revenue: 0, cost: 0, profit: 0, pct: 0, estHours: 0, actualHours: 0, foamLb: 0, sqftInch: 0, foamPerSqftInch: null, foamCostPerSqftInch: null, _q: new Set() });
    g.lines++; g.revenue += num(r.amount); g.cost += num(r.cost); g.profit += num(r.profit); g.estHours += num(r.laborHours); g.actualHours += num(r.actualHours);
    if (r.jobType === "lift" && num(r.foamLb) > 0 && num(r.sqftInch) > 0) { g.foamLb += num(r.foamLb); g.sqftInch += num(r.sqftInch); }
    if (r.quoteId) g._q.add(String(r.quoteId));
  });
  const perLb = num(opts.foamCostPerLb);
  return Object.values(by).map((g) => {
    const { _q, ...rest } = g;
    const fp = g.sqftInch > 0 ? g.foamLb / g.sqftInch : null;
    return { ...rest, quotes: _q.size || g.lines, revenue: r2(g.revenue), cost: r2(g.cost), profit: r2(g.profit), pct: g.revenue > 0 ? Math.round((g.profit / g.revenue) * 100) : 0,
      estHours: r2(g.estHours), actualHours: r2(g.actualHours), foamLb: r2(g.foamLb), sqftInch: Math.round(g.sqftInch),
      foamPerSqftInch: fp == null ? null : Math.round(fp * 1000) / 1000, foamCostPerSqftInch: fp == null || !(perLb > 0) ? null : r2(fp * perLb) };
  }).sort((a, b) => b.revenue - a.revenue || JOB_ORDER.indexOf(a.jobType) - JOB_ORDER.indexOf(b.jobType));
}

/* ---- where the leads come from ---------------------------------------
   A concrete quote's source is thin: the website request lands as "widget"
   with the real channel in inputs.concrete.request.heardFrom; a care plan's
   rebook says "careplan"; the receptionist's says "ai-call". */
export const CX_SOURCE_MAP: Record<string, { key: string; label: string }> = {
  google: { key: "google", label: "Google / LSA" }, "yard-sign": { key: "yardsign", label: "Yard sign" }, yardsign: { key: "yardsign", label: "Yard sign" },
  "door-hanger": { key: "doorhanger", label: "Door hanger" }, neighbor: { key: "referral", label: "Referral / neighbor" }, referral: { key: "referral", label: "Referral / neighbor" },
  facebook: { key: "social", label: "Facebook / Instagram" }, social: { key: "social", label: "Facebook / Instagram" }, nextdoor: { key: "nextdoor", label: "Nextdoor" }, truck: { key: "truck", label: "Truck wrap" },
  website: { key: "website", label: "Website / widget" }, widget: { key: "website", label: "Website / widget" }, booking: { key: "booking", label: "Online booking" },
  careplan: { key: "careplan", label: "Care plan (repeat)" }, repeat: { key: "repeat", label: "Repeat customer" }, "ai-call": { key: "phone", label: "Phone (AI line)" }, phone: { key: "phone", label: "Phone" },
  homeshow: { key: "homeshow", label: "Home show" }, leadradar: { key: "leadradar", label: "Lead Radar" }, other: { key: "other", label: "Other" },
};
export function concreteSourceOf(q: any): { key: string; label: string } {
  const raw = String((q && q.source) || "").toLowerCase();
  const heard = String((q && q.inputs && q.inputs.concrete && q.inputs.concrete.request && q.inputs.concrete.request.heardFrom) || "").toLowerCase();
  if ((raw === "widget" || raw === "website") && heard && CX_SOURCE_MAP[heard]) return CX_SOURCE_MAP[heard];
  return CX_SOURCE_MAP[raw] || (heard && CX_SOURCE_MAP[heard]) || { key: "unknown", label: "Unknown / not tagged" };
}
/* leads, wins and revenue per source, with the job types each source brings in */
export function sourceRows(quotes: any[], isWon: (q: any) => boolean): { key: string; label: string; leads: number; won: number; closeRate: number; revenue: number; jobTypes: Record<string, { leads: number; won: number }> }[] {
  const by: Record<string, any> = {};
  (quotes || []).forEach((q) => {
    if (!q) return;
    const b = concreteSourceOf(q);
    const g = by[b.key] || (by[b.key] = { key: b.key, label: b.label, leads: 0, won: 0, revenue: 0, jobTypes: {} });
    const won = isWon(q);
    g.leads++; if (won) { g.won++; g.revenue += num(q.total); }
    (Array.isArray(q.jobTypes) ? q.jobTypes : []).forEach((jt: string) => { if (!JOB_ORDER.includes(jt)) return; const t = g.jobTypes[jt] || (g.jobTypes[jt] = { leads: 0, won: 0 }); t.leads++; if (won) t.won++; });
  });
  return Object.values(by).map((g: any) => ({ ...g, revenue: r2(g.revenue), closeRate: g.leads ? Math.round((g.won / g.leads) * 100) : 0 })).sort((a: any, b: any) => b.revenue - a.revenue || b.leads - a.leads);
}

/* ---- the all-services roll-up: fence beside concrete ------------------ */
export function servicesRollup(allQuotes: any[], allWorkOrders: any[], from: number, verticalOf: (r: any) => string, isWon: (q: any) => boolean) {
  const by: Record<string, any> = {};
  const g = (v: string) => by[v] || (by[v] = { vertical: v, quotes: 0, won: 0, winRate: 0, revenue: 0, avgJob: 0, pipeline: 0, backlog: 0, completed: 0 });
  (allQuotes || []).forEach((q) => {
    if (!q || num(q.createdAt) < (num(from) || 0)) return;
    const row = g(verticalOf(q));
    row.quotes++;
    if (isWon(q)) { row.won++; row.revenue += num(q.total); }
    else if (q.status === "pending" || q.status === "viewed" || q.status === "sent") row.pipeline += num(q.total);
  });
  (allWorkOrders || []).forEach((w) => {
    if (!w) return;
    const row = g(verticalOf(w));
    if (w.status === "scheduled" || w.status === "in_progress") row.backlog += num(w.total);
    if (w.status === "completed" && num(w.completedAt) >= (num(from) || 0)) row.completed++;
  });
  return Object.values(by).map((r: any) => ({ ...r, revenue: r2(r.revenue), pipeline: r2(r.pipeline), backlog: r2(r.backlog), winRate: r.quotes ? Math.round((r.won / r.quotes) * 100) : 0, avgJob: r.won ? r2(r.revenue / r.won) : 0 }))
    .sort((a: any, b: any) => b.revenue - a.revenue);
}
